import axios from "axios";
import { useState } from "react";
import { FaXmark } from "react-icons/fa6";
import { toast } from "react-toastify";
import { API_ROOT } from "../../../config";
import { getCookie } from "cookies-next";

const DocumentVerify = () => {
  const [formData, setFormData] = useState({
    no_order: "",
    file_document: null,
  });
  const [formWarnings, setFormWarnings] = useState({
    no_order: null,
    file_document: null,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== "application/pdf") {
        toast.error("Error: File must be in PDF format!");
        setFormWarnings((prev) => ({
          ...prev,
          file_document: "File must be a PDF",
        }));
        return;
      }
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error("Error: File size must not exceed 5 MB!");
        setFormWarnings((prev) => ({
          ...prev,
          file_document: "File size must not exceed 5 MB",
        }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData((prev) => ({
          ...prev,
          file_document: file,
        }));
        setPdfPreviewUrl(event.target.result);
        setFormWarnings((prev) => ({
          ...prev,
          file_document: null,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    let isValid = true;
    const warnings = { ...formWarnings };

    if (!formData.no_order.trim()) {
      warnings.no_order = "Order number is required";
      isValid = false;
    } else {
      warnings.no_order = null;
    }

    if (!formData.file_document) {
      warnings.file_document = "PDF file is required";
      isValid = false;
    } else {
      warnings.file_document = null;
    }

    setFormWarnings(warnings);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64File = reader.result.split(",")[1];

        const payload = {
          order_id: formData.no_order,
          file_document: base64File,
        };

        try {
          const { data } = await axios.post(
            API_ROOT + "/admin/document-signature/verify",
            payload,
            {
              headers: {
                Authorization: getCookie("admin_auth"),
              },
            }
          );

          console.log(data);
        } catch (error) {
          console.log(error);
        } finally {
          setIsSubmitting(false);
        }
      };

      reader.onerror = (error) => {
        toast.error("Error converting file:", error);
        setIsSubmitting(false);
      };
      reader.readAsDataURL(formData.file_document);
    } catch (error) {
      console.log(error);
      setIsSubmitting(false);
    }

    // setTimeout(() => {
    //   const isSuccess = Math.random() > 0.5;
    //   setVerificationResult({
    //     status: isSuccess ? "success" : "failure",
    //     message: isSuccess
    //       ? "Document verification completed successfully."
    //       : "Document verification failed due to invalid signature.",
    //   });
    //   setModalOpen(true);
    //   setIsSubmitting(false);
    //   toast.success(
    //     isSuccess ? "Verification Successful!" : "Verification Failed!"
    //   );
    // }, 1000);
  };

  const closeModal = () => {
    setModalOpen(false);
    setVerificationResult(null);
    setFormData({ no_order: "", file_document: null });
    setPdfPreviewUrl(null);
    setFormWarnings({ no_order: null, file_document: null });
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <h1 className="text-2xl font-semibold mb-6 text-center">
          Document Verification
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col gap-2">
            <label htmlFor="no_order" className="font-medium text-gray-700">
              Order ID *
            </label>
            <input
              id="no_order"
              type="text"
              value={formData.no_order}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, no_order: e.target.value }))
              }
              placeholder="Enter order number"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                formWarnings.no_order
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
            />
            {formWarnings.no_order && (
              <p className="text-xs text-red-500">{formWarnings.no_order}</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <label
              htmlFor="file_document"
              className="font-medium text-gray-700"
            >
              Document (PDF) *
            </label>
            <input
              id="file_document"
              type="file"
              accept="application/pdf"
              onChange={handleFileUpload}
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 ${
                formWarnings.file_document
                  ? "border-red-500 focus:ring-red-500"
                  : "border-gray-300 focus:ring-blue-500"
              }`}
            />
            <span className="text-xs text-gray-500">
              *Format: <strong>PDF</strong>, max size: <strong>5 MB</strong>
            </span>
            {formWarnings.file_document && (
              <p className="text-xs text-red-500">
                {formWarnings.file_document}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className={`w-full py-2 px-4 rounded-md text-white ${
              isSubmitting
                ? "bg-blue-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            } transition-colors duration-200`}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </button>
        </form>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">
                Verification{" "}
                {verificationResult.status === "success"
                  ? "Success"
                  : "Failure"}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-600 hover:text-gray-800"
                aria-label="Close modal"
              >
                <FaXmark className="w-5 h-5" />
              </button>
            </div>
            <hr className="mb-4" />
            <p
              className={`text-gray-700 mb-4 ${
                verificationResult.status === "success"
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {verificationResult.message}
            </p>
            {pdfPreviewUrl && (
              <div className="mt-4">
                <h3 className="font-medium text-gray-700 mb-2">PDF Preview</h3>
                <embed
                  src={pdfPreviewUrl}
                  type="application/pdf"
                  className="w-full h-96 border rounded-md"
                  title="PDF Preview"
                />
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button
                onClick={closeModal}
                className="py-2 px-4 rounded-md text-sm border border-gray-300 text-gray-600 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentVerify;
